import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Zap, ShieldCheck, Info, Eye, ThumbsUp, Twitter, MessagesSquare, Share2, Rocket, Lock } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { safeUrl } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { RankBadge, ScoreDonut, ClaimBadge, VerifiedBadge, Stars, ProAlumniBadge } from "@/components/SignalBadges";
import BoostPricingModal from "@/components/BoostPricingModal";
import ShareProfileModal from "@/components/ShareProfileModal";
import OwnerStudio from "@/components/OwnerStudio";
import { getSessionId } from "@/components/LiveCounter";
import { FadeUp } from "@/components/Reveal";

function formatCount(n) {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function BreakdownBar({ label, pts, max, color, delay }) {
  return (
    <div>
      <div className="flex justify-between font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">
        <span>{label}</span>
        <span className="text-slate-300">
          {pts.toFixed(1)} / {max}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${(pts / max) * 100}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
          className={`h-full rounded-full ${color}`}
        />
      </div>
    </div>
  );
}

export default function CompanyProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [endorsing, setEndorsing] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewBody, setReviewBody] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [freeBoostStatus, setFreeBoostStatus] = useState(null); // null/pending/approved/rejected/revoked/expired
  const [freeBoostBusy, setFreeBoostBusy] = useState(false);
  const [socialStats, setSocialStats] = useState(null);

  const loadFreeBoostStatus = useCallback(() => {
    if (!user) return;
    api.get(`/companies/${slug}/boost-request-status`).then((r) => setFreeBoostStatus(r.data.status)).catch(() => {});
  }, [slug, user]);

  const load = useCallback(() => {
    api
      .get(`/companies/${slug}`)
      .then((r) => setCompany(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      });
  }, [slug]);

  useEffect(load, [load]);
  useEffect(loadFreeBoostStatus, [loadFreeBoostStatus]);

  // Fetch live social stats (Reddit karma, X followers) once company loads
  useEffect(() => {
    if (company && (company.social_x || company.social_reddit)) {
      api
        .get(`/companies/${slug}/social-stats`)
        .then((r) => setSocialStats(r.data))
        .catch(() => {});
    }
  }, [company?.social_x, company?.social_reddit, slug]);

  useEffect(() => {
    const key = `hr-viewed-${slug}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      api.post(`/companies/${slug}/view`, { session_id: getSessionId() }).then(load).catch(() => {});
    }
  }, [slug, load]);

  useEffect(() => {
    setReviewRating(company?.my_review?.rating || 0);
    setReviewBody(company?.my_review?.body || "");
  }, [company?.my_review?.rating, company?.my_review?.body]);

  // Handle Stripe boost redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const boostSuccess = params.get("boost_success");
    const sessionId = params.get("session_id");
    const boostCancelled = params.get("boost_cancelled");

    if (boostCancelled) {
      toast.info("Boost checkout was cancelled.");
      navigate(`/company/${slug}`, { replace: true });
      return;
    }

    if (boostSuccess && sessionId) {
      const confirm = async () => {
        try {
          // Poll until payment is confirmed (up to 10 seconds)
          let paid = false;
          for (let i = 0; i < 5; i++) {
            const { data } = await api.get(`/payments/status/${sessionId}`);
            if (data.payment_status === "paid") { paid = true; break; }
            await new Promise((r) => setTimeout(r, 2000));
          }
          if (paid) {
            await api.post(`/companies/${slug}/boost/confirm`, { session_id: sessionId });
            toast.success("Payment received! Your Boost is pending admin approval.");
            load();
          } else {
            toast.error("Payment not confirmed yet. Please refresh in a moment.");
          }
        } catch {
          toast.error("Could not confirm payment. Please contact support.");
        }
      };
      confirm();
      navigate(`/company/${slug}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/auth", { state: { from: location.pathname } });
      return;
    }
    if (!reviewRating) {
      toast.error("Choose a star rating first.");
      return;
    }
    if (reviewBody.trim().length < 10) {
      toast.error("Written feedback must be at least 10 characters.");
      return;
    }
    setReviewSaving(true);
    try {
      await api.post(`/companies/${slug}/reviews`, { rating: reviewRating, body: reviewBody.trim() });
      toast.success("Review saved — HighRank Score updated.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReviewSaving(false);
    }
  };

  const toggleEndorse = async () => {
    if (endorsing) return;
    setEndorsing(true);
    try {
      const { data } = await api.post(`/companies/${slug}/upvote`);
      toast.success(data.upvoted ? "Endorsed — HighRank Score updated." : "Endorsement removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEndorsing(false);
    }
  };

  if (notFound) {
    return (
      <main className="pt-28 md:pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="font-display text-5xl text-slate-100 mb-3">OFF THE BOARD</p>
          <p className="text-slate-500 mb-6">This company doesn't exist on HighRank.</p>
          <Link to="/leaderboard" data-testid="notfound-back-link" className="text-emerald-400 hover:underline text-sm">
            ← Back to the leaderboard
          </Link>
        </div>
      </main>
    );
  }

  if (!company) {
    return (
      <main className="pt-28 md:pt-16 min-h-screen flex items-center justify-center">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-600 animate-pulse">Loading profile…</p>
      </main>
    );
  }

  const b = company.score_breakdown;
  const socialUrls = [
    company.social_x ? `https://x.com/${company.social_x}` : null,
    company.social_reddit ? `https://reddit.com/user/${company.social_reddit}` : null,
  ].filter(Boolean);
  const socialSummary = [
    company.social_x ? `X @${company.social_x}` : null,
    company.social_reddit ? `Reddit u/${company.social_reddit}` : null,
  ].filter(Boolean);
  const shareDescription = [
    `${company.name} on HighRank: Rank Position #${company.rank_position}, ${company.highrank_score !== null ? `HighRank Score ${company.highrank_score}/100` : "not yet scored"}.`,
    company.tagline,
    socialSummary.length ? `Social: ${socialSummary.join(" · ")}` : null,
  ].filter(Boolean).join(" ");

  return (
    <main data-testid="company-profile-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo
        title={`${company.name} — HighRank Profile`}
        siteName="HighRank"
        description={shareDescription}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: company.name,
          url: company.website || undefined,
          sameAs: socialUrls.length ? socialUrls : undefined,
        }}
      />

      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-emerald-500/10 blur-[110px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <Link
            to="/leaderboard"
            data-testid="back-to-leaderboard-link"
            className="inline-flex items-center gap-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 hover:text-emerald-400 transition-colors mb-5"
          >
            <ArrowLeft size={13} /> Leaderboard
          </Link>

          <FadeUp>
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <ClaimBadge claimed={company.claimed} testId="profile-claim-badge" />
                  {company.verified && <VerifiedBadge testId="profile-verified-badge" />}
                  {company.pro_alumni && <ProAlumniBadge testId="profile-pro-alumni-badge" />}}
                  <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500 border border-white/10 rounded px-2 py-1">
                    {company.category}
                  </span>
                  <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500 border border-white/10 rounded px-2 py-1">
                    {company.region}
                  </span>
                  <span
                    data-testid="profile-views"
                    className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500 border border-white/10 rounded px-2 py-1 inline-flex items-center gap-1"
                  >
                    <Eye size={11} /> {company.views || 0} views
                  </span>
                  {company.social_x && (
                    <a
                      href={`https://x.com/${company.social_x}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${company.name} on X`}
                      data-testid="social-x-link"
                      className="inline-flex items-center gap-1.5 border border-white/10 rounded px-2 py-1 text-slate-500 hover:text-slate-200 hover:border-white/20 transition-colors"
                    >
                      <Twitter size={11} />
                      <span className="font-mono2 text-[10px] uppercase tracking-widest">
                        {socialStats?.x_followers != null
                          ? `${formatCount(socialStats.x_followers)} followers`
                          : `@${company.social_x}`}
                      </span>
                    </a>
                  )}
                  {company.social_reddit && (
                    <a
                      href={`https://reddit.com/user/${company.social_reddit}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${company.name} on Reddit`}
                      data-testid="social-reddit-link"
                      className="inline-flex items-center gap-1.5 border border-white/10 rounded px-2 py-1 text-slate-500 hover:text-slate-200 hover:border-white/20 transition-colors"
                    >
                      <MessagesSquare size={11} />
                      <span className="font-mono2 text-[10px] uppercase tracking-widest">
                        {socialStats?.reddit_karma != null
                          ? `${formatCount(socialStats.reddit_karma)} karma`
                          : `u/${company.social_reddit}`}
                      </span>
                    </a>
                  )}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-wide text-slate-100 leading-[0.95]">
                  {company.name}
                </h1>
                <p className="mt-2 text-base sm:text-lg text-slate-400 max-w-xl">{company.tagline}</p>
                {company.website && (
                  <a
                    href={safeUrl(company.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="company-website-link"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm text-green-600 hover:text-green-500 transition-colors font-mono2"
                  >
                    {company.website.replace(/^https?:\/\//, "")} <ExternalLink size={13} />
                  </a>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  {company.is_owner && company.claimed && (
                    <button
                      data-testid="boost-button"
                      onClick={() => setBoostOpen(true)}
                      className="inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 font-bold text-sm rounded-full px-6 py-2.5 transition-all duration-200"
                    >
                      <Zap size={15} /> Boost Top Spot
                    </button>
                  )}

                  {/* Free Boost Request — only for claimed owners */}
                  {company.is_owner && company.claimed && (
                    <>
                      {(freeBoostStatus === null) && (
                        <button
                          data-testid="free-boost-request-btn"
                          disabled={freeBoostBusy}
                          onClick={async () => {
                            setFreeBoostBusy(true);
                            try {
                              const { data } = await api.post(`/companies/${slug}/request-free-boost`);
                              toast.success(data.message || "Request submitted!");
                              setFreeBoostStatus("pending");
                            } catch (e) {
                              toast.error(formatApiError(e));
                            } finally {
                              setFreeBoostBusy(false);
                            }
                          }}
                          className="inline-flex items-center gap-2 border border-amber-500/60 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 font-bold text-sm rounded-full px-6 py-2.5 transition-all duration-200"
                        >
                          <Rocket size={15} /> {freeBoostBusy ? "Submitting…" : "Request Free Boost Week"}
                        </button>
                      )}
                      {freeBoostStatus === "pending" && (
                        <div data-testid="free-boost-pending" className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 text-amber-500 text-xs font-medium rounded-full px-4 py-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> Boost request in review
                        </div>
                      )}
                    </>
                  )}

                  {company.share_card_unlocked ? (
                    <button
                      data-testid="profile-share-button"
                      onClick={() => setShareOpen(true)}
                      className="inline-flex items-center gap-2 border border-yellow-400 bg-yellow-400 hover:bg-yellow-300 hover:border-yellow-300 text-black font-bold text-sm rounded-full px-6 py-2.5 transition-all duration-200 shadow-[0_0_12px_rgba(234,179,8,0.35)]"
                    >
                      <Share2 size={15} /> Share card
                    </button>
                  ) : (
                    <div
                      data-testid="profile-share-button-locked"
                      title="Share Card unlocks permanently after your first Pro Boost"
                      className="group relative inline-flex items-center gap-2 border border-white/10 bg-white/[0.03] text-slate-600 font-bold text-sm rounded-full px-6 py-2.5 cursor-not-allowed select-none"
                    >
                      <Lock size={13} className="text-slate-700" />
                      <span>Share card</span>
                      <span className="ml-1 text-[10px] font-mono uppercase tracking-widest text-yellow-700/60 border border-yellow-700/20 rounded-full px-1.5 py-0.5 leading-none">Pro</span>
                      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 rounded-lg bg-white border border-slate-300 px-3 py-2 text-xs text-slate-900 font-semibold text-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50">
                        Unlocks permanently with a Pro Boost
                      </span>
                    </div>
                  )}
                  {company.ownership_status === "unclaimed" && (
                    <Link
                      to={`/claim/${company.slug}`}
                      data-testid="claim-business-button"
                      className="inline-flex items-center gap-2 border border-white/20 hover:border-emerald-500/50 text-slate-200 hover:text-emerald-400 font-semibold text-sm rounded-full px-6 py-2.5 transition-all duration-200"
                    >
                      <ShieldCheck size={15} /> Claim this business
                    </Link>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-6 rounded-2xl border border-white/[0.07] bg-[#0A0A09]/90 p-5">
                <div className="text-center">
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-slate-600 mb-2">Rank Position</p>
                  <RankBadge rank={company.rank_position} boosted={company.boosted} testId="profile-rank-badge" />
                  <p className="font-mono2 text-[9px] text-slate-700 mt-2">BOARD POSITION</p>
                </div>
                <div className="w-px h-16 bg-white/10" />
                <div className="text-center">
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-slate-600 mb-2">HighRank Score</p>
                  <div className="flex justify-center">
                    <ScoreDonut score={company.highrank_score} size={68} testId="profile-score-badge" />
                  </div>
                  <p className="font-mono2 text-[9px] text-slate-700 mt-2">EARNED BY ENDORSEMENTS</p>
                </div>
              </div>
            </div>

            {!company.claimed && (
              <div
                data-testid="unclaimed-disclaimer"
                className="mt-5 flex items-start gap-2.5 rounded-xl border border-white/10 bg-[#0A0A09] px-4 py-3 max-w-2xl"
              >
                <Info size={14} className="text-slate-500 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  This listing was published by HighRank and has not been claimed by {company.name}. Being listed
                  implies no endorsement of, or partnership with, HighRank.
                </p>
              </div>
            )}

            {!company.claimed && company.rank_position <= 3 && (
              <p
                data-testid="top3-claim-hint"
                className="mt-3 text-xs text-amber-400/90 font-mono2 uppercase tracking-wider"
              >
                Claim this profile and sign in to manage your account.
              </p>
            )}

            {company.is_owner && (
              <div className="max-w-2xl">
                <OwnerStudio company={company} onChanged={load} />
              </div>
            )}
          </FadeUp>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[1fr_1.4fr] gap-6">
        {/* About & Makers + Score breakdown */}
        <FadeUp>
          <div className="space-y-5">
            {(company.description || company.makers?.length > 0) && (
              <div className="space-y-4">
                {company.description && (
                  <div data-testid="about-section" className="rounded-2xl border border-cyan-500/15 bg-gradient-to-br from-cyan-500/[0.06] to-transparent p-6">
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-slate-800 mb-2.5 flex items-center gap-1.5 font-bold">
                      <Info size={11} className="text-cyan-600" /> About
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">{company.description}</p>
                  </div>
                )}
                {company.makers?.length > 0 && (
                  <div data-testid="makers-section" className="rounded-2xl border border-violet-500/15 bg-gradient-to-br from-violet-500/[0.06] to-transparent p-6">
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-violet-400/80 mb-3">Makers</p>
                    <div className="flex flex-wrap gap-2">
                      {company.makers.map((maker, i) => (
                        <span
                          key={i}
                          data-testid={`maker-${i}`}
                          className="inline-flex items-center gap-1.5 bg-violet-500/10 border border-violet-500/20 text-violet-300 text-xs font-medium rounded-full px-3 py-1.5"
                        >
                          <span className="w-4 h-4 rounded-full bg-violet-500/30 flex items-center justify-center text-[9px] font-bold text-violet-200">
                            {maker.charAt(0).toUpperCase()}
                          </span>
                          {maker}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-6" data-testid="score-breakdown-card">
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-2">
              {"// Score breakdown"}
            </p>
            <h2 className="font-heading text-xl font-bold text-slate-100 mb-5">How the HighRank Score is built</h2>
            {b ? (
              <div className="space-y-4">
                <BreakdownBar label="Average star rating — 40%" pts={b.rating} max={b.rating_max} color="bg-emerald-500" delay={0.05} />
                <BreakdownBar label="Written review volume — 20%" pts={b.review_volume} max={b.review_volume_max} color="bg-cyan-500" delay={0.1} />
                <BreakdownBar label="Endorsements (thumbs up) — 20%" pts={b.endorsements} max={b.endorsements_max} color="bg-emerald-300" delay={0.15} />
                <BreakdownBar label="Profile completeness — 20%" pts={b.profile_completeness} max={b.profile_completeness_max} color="bg-slate-400" delay={0.2} />
                <div className="pt-3 border-t border-white/5 flex items-center justify-between gap-4 flex-wrap">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 inline-flex items-center gap-1.5">
                    <ThumbsUp size={11} /> {company.upvotes} endorsement{company.upvotes === 1 ? "" : "s"} · {company.review_count} review{company.review_count === 1 ? "" : "s"}
                  </span>
                  <span className="font-mono2 text-2xl font-bold text-emerald-400">{company.highrank_score}</span>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 bg-[#121210] p-5 text-center">
                <p className="font-mono2 text-sm text-slate-400 uppercase tracking-widest mb-2">Not yet scored</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Zero reviews or endorsements so far — we don't publish a 0/100. The first genuine signal starts the score.
                </p>
              </div>
            )}
            <p className="mt-5 text-[11px] text-slate-600 leading-relaxed">
              HighRank Score is HighRank's own platform score — built from auth-gated star reviews, written feedback,
              thumbs-up endorsements and profile quality. It is never affected by Boost.
            </p>
          </div>
          </div>
        </FadeUp>

        {/* Reviews & endorsements */}
        <FadeUp delay={0.08}>
          <div data-testid="reviews-section">
            <h2 className="font-heading text-xl font-bold text-slate-100 mb-4 flex items-center gap-2 flex-wrap">
              Reviews & endorsements{" "}
              <span data-testid="review-count" className="font-mono2 text-sm text-slate-500">
                ({company.review_count} reviews · {company.upvotes} thumbs up)
              </span>
              {company.average_rating !== null && (
                <span data-testid="average-rating" className="inline-flex items-center gap-2 font-mono2 text-sm text-emerald-400">
                  <Stars value={company.average_rating} size={12} /> {company.average_rating}/5
                </span>
              )}
            </h2>

            <div className="grid gap-4 mb-6">
              <div className="rounded-2xl border border-emerald-500/20 bg-[#0A0A09] p-6">
                <p className="font-heading font-semibold text-slate-100 mb-1 flex items-center gap-2">
                  <ThumbsUp size={15} className="text-emerald-400" /> Quick endorsement
                </p>
                <p className="text-xs text-slate-500 leading-relaxed mb-5">
                  One thumbs up per account. Click again to withdraw it.
                </p>
                {user ? (
                  <button
                    data-testid="endorse-button"
                    onClick={toggleEndorse}
                    disabled={endorsing}
                    className={`inline-flex items-center gap-2.5 font-bold text-sm rounded-full px-8 py-3.5 transition-all duration-200 disabled:opacity-50 ${
                      company.upvoted
                        ? "bg-emerald-500 text-black shadow-[0_0_25px_rgba(16,185,129,0.35)]"
                        : "border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                    }`}
                  >
                    <ThumbsUp size={18} className={company.upvoted ? "fill-black" : ""} />
                    {company.upvoted ? "Endorsed" : "Thumbs up"} · {company.upvotes}
                  </button>
                ) : (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-sm text-slate-400">Sign in to endorse — one per person, always real.</p>
                    <button
                      data-testid="endorse-signin-cta"
                      onClick={() => navigate("/auth", { state: { from: location.pathname } })}
                      className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-full px-6 py-2.5 transition-colors duration-200"
                    >
                      Sign in to endorse
                    </button>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-6">
                <p className="font-heading font-semibold text-slate-100 mb-1">Write a review</p>
                <p className="text-xs text-slate-500 leading-relaxed mb-5">
                  One review per account. Star rating and written feedback are both required, and you can update yours anytime.
                </p>
                {user ? (
                  <form data-testid="review-form" onSubmit={submitReview} className="space-y-4">
                    <div>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Your rating</p>
                      <Stars value={reviewRating} onSelect={setReviewRating} testId="review-star-rating" />
                    </div>
                    <textarea
                      data-testid="review-body-input"
                      value={reviewBody}
                      onChange={(e) => setReviewBody(e.target.value)}
                      minLength={10}
                      maxLength={2000}
                      required
                      rows={4}
                      placeholder="What should founders and customers know?"
                      className="w-full rounded-xl border border-white/10 bg-[#121210] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 transition-colors resize-y"
                    />
                    <button
                      type="submit"
                      data-testid="review-submit-button"
                      disabled={reviewSaving}
                      className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold text-sm rounded-full px-6 py-2.5 transition-colors duration-200"
                    >
                      {reviewSaving ? "Saving…" : company.my_review ? "Update review" : "Publish review"}
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-sm text-slate-400">Sign in to leave a star rating and written feedback.</p>
                    <button
                      data-testid="review-signin-cta"
                      onClick={() => navigate("/auth", { state: { from: location.pathname } })}
                      className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-full px-6 py-2.5 transition-colors duration-200"
                    >
                      Sign in to review
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 mb-6" data-testid="reviews-list">
              {company.reviews.length === 0 ? (
                <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-6 text-center">
                  <p className="text-sm text-slate-500">No written reviews yet.</p>
                </div>
              ) : (
                company.reviews.map((review) => (
                  <article
                    key={review.id}
                    data-testid={`review-card-${review.id}`}
                    className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-5"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-heading font-bold text-sm flex items-center justify-center">
                          {review.author_name.slice(0, 1).toUpperCase()}
                        </span>
                        <div>
                          <p className="font-heading font-semibold text-sm text-slate-100">{review.author_name}</p>
                          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600">
                            {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Stars value={review.rating} size={13} testId={`review-stars-${review.id}`} />
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">{review.body}</p>
                  </article>
                ))
              )}
            </div>

            <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-6">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-slate-600 mb-3">
                {"// How reputation works"}
              </p>
              <ul className="space-y-2.5 text-sm text-slate-400 leading-relaxed">
                <li className="flex gap-3">
                  <span className="text-emerald-500 shrink-0">—</span> Star ratings, written reviews and thumbs-up
                  endorsements all require a signed-in account.
                </li>
                <li className="flex gap-3">
                  <span className="text-emerald-500 shrink-0">—</span> Score: 40% average rating, 20% review volume,
                  20% endorsements, 20% profile completeness.
                </li>
                <li className="flex gap-3">
                  <span className="text-emerald-500 shrink-0">—</span> Boost can lift Top Spot placement, but it can never
                  buy reputation.
                </li>
              </ul>
            </div>
          </div>
        </FadeUp>
      </section>

      <BoostPricingModal open={boostOpen} onOpenChange={setBoostOpen} company={company} />
      <ShareProfileModal open={shareOpen} onOpenChange={setShareOpen} company={company} />
    </main>
  );
}


